import { lazy } from "react";
import { useSelector } from "react-redux";
import { generatePath, Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import Layout from "_metronic/layout/Layout";

import { RouteErrorBoundary } from "app/common/ErrorBoundary";
import WorkspaceBootstrapGate from "app/common/workspace/WorkspaceBootstrapGate";
import { canShowOnboarding } from "app/pages/auth/onboardingEligibility";

import AppWrapper from "./AppWrapper";
import { AuthRoute, ProtectedRoute } from "./ProtectedRoute";

// Lazy load route components for code splitting
const HomePage = lazy(() => import("app/pages/home/HomePage"));
const LogoutPage = lazy(() => import("app/pages/auth/Logout"));
const AuthPage = lazy(() => import("app/pages/auth/AuthPage"));
const Provider = lazy(() => import("app/pages/provider"));
const GoogleDriveConnected = lazy(() => import("app/pages/GoogleDriveConnected"));
const OneDriveConnected = lazy(() => import("app/pages/OneDriveConnected"));
const Onboarding = lazy(() => import("app/pages/auth/Onboarding"));

/**
 * Root route component that wraps all routes with AppWrapper
 * This ensures AppWrapper (context providers, global components) is available to all routes
 */
function RootRoute() {
  return (
    <AppWrapper>
      <Outlet />
    </AppWrapper>
  );
}

/**
 * Boundary for routes that render inside the authenticated shell. It sits under
 * <Layout>, so a render failure leaves the header, sidebar and navigation intact
 * and the user can move elsewhere instead of facing a blank app.
 *
 * Keyed on the pathname: React error boundaries do not reset themselves, so
 * without this a broken route would keep showing its fallback after navigating
 * away.
 */
function BoundedRoute({ children }) {
  const { pathname } = useLocation();

  return <RouteErrorBoundary resetKey={pathname}>{children}</RouteErrorBoundary>;
}

function LegacyResetRedirect() {
  const { user, token } = useParams();

  return <Navigate to={generatePath("/auth/reset/:user/:token", { user, token })} replace />;
}

function AuthenticatedApplication() {
  const onboardingCompleted = useSelector((state) => state.auth.user?.onboardingCompleted);

  if (canShowOnboarding({ onboardingCompleted })) return <Navigate to="/onboarding" replace />;

  return (
    <Layout>
      <WorkspaceBootstrapGate>
        <BoundedRoute>
          <HomePage />
        </BoundedRoute>
      </WorkspaceBootstrapGate>
    </Layout>
  );
}

/**
 * Route configuration for createBrowserRouter
 * This needs to be a static configuration, so we use wrapper components
 * for dynamic logic (auth checks, etc.)
 */
export function createRoutes() {
  return [
    {
      element: <RootRoute />,
      children: [
        {
          path: "/logout",
          element: <LogoutPage />,
        },
        {
          path: "/provider/:request/:provider",
          element: (
            <BoundedRoute>
              <Provider />
            </BoundedRoute>
          ),
        },
        {
          path: "/google-drive-connected",
          element: <GoogleDriveConnected />,
        },
        {
          path: "/onedrive-connected",
          element: <OneDriveConnected />,
        },
        {
          path: "/auth/*",
          element: (
            <AuthRoute>
              <AuthPage />
            </AuthRoute>
          ),
        },
        {
          path: "/onboarding",
          element: (
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          ),
        },
        {
          path: "/reset/:user/:token",
          element: <LegacyResetRedirect />,
        },
        {
          path: "*",
          element: (
            <ProtectedRoute>
              <AuthenticatedApplication />
            </ProtectedRoute>
          ),
        },
      ],
    },
  ];
}
